/**
 * Exclusive Offer Section & Modal Logic
 * - Manages modal open/close transitions
 * - Locks body scroll with scrollbar-shift prevention
 * - Bulletproof smooth scrolling to target on-page form (e.g. #bookingForm, #book-test-ride-offers)
 * - Supports popup modal triggers & standard link navigation
 * - Shopify Theme Editor block selection sync
 */
(function () {
  'use strict';

  function getScrollbarWidth() {
    return window.innerWidth - document.documentElement.clientWidth;
  }

  function unlockBodyScroll() {
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
    document.documentElement.style.overflow = '';
    document.body.classList.remove('btr-modal-open');
    document.documentElement.classList.remove('btr-modal-open');
  }

  function openOfferModal(modal, trigger) {
    if (!modal) return;

    // Lock body scroll with scrollbar compensation
    const sbw = getScrollbarWidth();
    if (sbw > 0) {
      document.body.style.paddingRight = `${sbw}px`;
    }
    document.body.style.overflow = 'hidden';

    modal.setAttribute('aria-hidden', 'false');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        modal.classList.add('is-open');
        const closeBtn = modal.querySelector('.exclusive-offer-modal__close');
        if (closeBtn) closeBtn.focus();
      });
    });
  }

  function closeOfferModal(modal, trigger, immediateUnlock) {
    if (!modal || !modal.classList.contains('is-open')) return;

    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');

    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
    } else {
      const activeTrigger = document.querySelector(`[data-offer-trigger="${modal.id}"]`);
      if (activeTrigger) activeTrigger.setAttribute('aria-expanded', 'false');
    }

    if (immediateUnlock) {
      unlockBodyScroll();
    } else {
      setTimeout(() => {
        const anyModalOpen = document.querySelector('.exclusive-offer-modal.is-open, .p360-offer-modal.is-open, .btr-modal-open');
        if (!anyModalOpen) {
          unlockBodyScroll();
        }
      }, 450);
    }
  }

  /**
   * Intelligently resolves any target ID or anchor selector
   * Handles cases where ID starts with '#', contains '#', or is just a plain ID.
   */
  function resolveTargetElement(href, scrollTargetAttr) {
    let raw = (scrollTargetAttr || '').trim();
    if (!raw && href) {
      raw = href.trim();
    }
    if (!raw) return null;

    // Extract hash part if full/relative URL was provided (e.g. /pages/festive-offer#bookingForm)
    if (raw.includes('#')) {
      const parts = raw.split('#');
      const hashPart = parts[parts.length - 1].trim();
      if (hashPart) raw = hashPart;
    }

    // If external URL without an on-page fragment
    if (raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('mailto:') || raw.startsWith('tel:')) {
      return null;
    }

    const cleanId = raw.replace(/^#+/, '');
    if (!cleanId) return null;

    // 1. Direct getElementById with cleanId
    let el = document.getElementById(cleanId);
    if (el) return el;

    // 2. Direct getElementById with '#' prefix (when HTML literally has id="#something")
    el = document.getElementById('#' + cleanId);
    if (el) return el;

    // 3. QuerySelector with exact attribute matching [id="..."]
    try {
      el = document.querySelector(`[id="${CSS.escape(cleanId)}"]`) ||
           document.querySelector(`[id="#${CSS.escape(cleanId)}"]`) ||
           document.querySelector(`[id="${cleanId}"]`) ||
           document.querySelector(`[id="#${cleanId}"]`);
      if (el) return el;
    } catch (e) {}

    // 4. Standard CSS ID selector
    try {
      el = document.querySelector('#' + CSS.escape(cleanId)) ||
           document.querySelector('#' + cleanId);
      if (el) return el;
    } catch (e) {}

    // 5. QuerySelector matching data-section-id or partial ID
    try {
      el = document.querySelector(`[data-section-id*="${cleanId}"]`) ||
           document.querySelector(`[id*="${cleanId}"]`);
      if (el) return el;
    } catch (e) {}

    // 6. Intelligent Fallback:
    // If target name relates to booking, test ride, or form, find the booking form section on this page!
    const lower = cleanId.toLowerCase();
    if (lower.includes('book') || lower.includes('ride') || lower.includes('form') || lower.includes('offer')) {
      el = document.getElementById('bookingForm') ||
           document.getElementById('#bookingForm') ||
           document.querySelector('.form-container-wrapper') ||
           document.querySelector('.book-test-ride-global') ||
           document.querySelector('[id*="book-test-ride"]') ||
           document.querySelector('form[action*="contact"]') ||
           document.querySelector('.section-book-test-ride');
      if (el) return el;
    }

    return null;
  }

  function scrollToTarget(targetEl) {
    if (!targetEl) return;

    // Immediately unlock body so scrolling can happen freely
    unlockBodyScroll();

    // Compute any fixed/sticky header height in the theme
    const header = document.querySelector('header.header') ||
                   document.querySelector('.header-wrapper') ||
                   document.querySelector('sticky-header') ||
                   document.querySelector('.site-header') ||
                   document.querySelector('header');
    const headerHeight = header ? header.getBoundingClientRect().height : 0;

    const rect = targetEl.getBoundingClientRect();
    const currentScrollY = window.pageYOffset || document.documentElement.scrollTop || window.scrollY || 0;
    const targetY = Math.max(0, Math.floor(currentScrollY + rect.top - headerHeight - 24));

    // Execute smooth scroll via window.scrollTo
    try {
      window.scrollTo({
        top: targetY,
        behavior: 'smooth'
      });
    } catch (e) {
      window.scrollTo(0, targetY);
    }

    // Safety fallback: If after 120ms scroll hasn't moved towards target, trigger scrollIntoView
    setTimeout(() => {
      const nowY = window.pageYOffset || document.documentElement.scrollTop || window.scrollY || 0;
      const diff = Math.abs(nowY - targetY);
      if (diff > 80 && Math.abs(nowY - currentScrollY) < 15) {
        try {
          targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } catch (err) {
          window.scrollTo(0, targetY);
        }
      }
    }, 120);

    // Update URL hash cleanly without causing a harsh jump
    const cleanId = (targetEl.id || '').replace(/^#+/, '');
    if (cleanId && window.history && window.history.pushState) {
      try {
        window.history.pushState(null, null, '#' + cleanId);
      } catch (e) {}
    }

    // Focus first input field in the form for high user conversion
    setTimeout(() => {
      const firstInput = targetEl.querySelector('select:not([disabled]), input:not([type="hidden"]):not([disabled])');
      if (firstInput) {
        try {
          firstInput.focus({ preventScroll: true });
        } catch (e) {
          try { firstInput.focus(); } catch (err) {}
        }
      }
    }, 550);
  }

  function handleCtaClick(e, ctaBtn) {
    const modal = ctaBtn.closest('.exclusive-offer-modal');
    const href = (ctaBtn.getAttribute('href') || '').trim();
    const scrollTargetAttr = (ctaBtn.getAttribute('data-scroll-target') || '').trim();

    // 1. Check if target is a popup modal trigger (e.g. #book-test-ride-popup)
    const isPopupTarget = (
      scrollTargetAttr === '#book-test-ride-popup' ||
      scrollTargetAttr === 'book-test-ride-popup' ||
      href === '#book-test-ride-popup' ||
      href === 'book-test-ride-popup'
    );
    const popupEl = isPopupTarget
      ? (document.getElementById('book-test-ride-popup') || document.querySelector('[data-btr-popup-id="book-test-ride-popup"]'))
      : null;

    if (popupEl) {
      e.preventDefault();
      e.stopPropagation();
      if (modal) closeOfferModal(modal, null, true);

      setTimeout(() => {
        const openBtn = document.querySelector('[data-popup-id="book-test-ride-popup"], [data-btr-popup-id="book-test-ride-popup"]');
        if (openBtn && typeof openBtn.click === 'function' && openBtn !== ctaBtn) {
          openBtn.click();
        } else {
          popupEl.classList.add('btr-modal-open');
          popupEl.style.display = 'flex';
        }
      }, 150);
      return;
    }

    // 2. Resolve on-page target element
    const targetEl = resolveTargetElement(href, scrollTargetAttr);

    if (targetEl) {
      e.preventDefault();
      e.stopPropagation();

      // Immediately unlock body scroll so scrolling can begin without delay
      unlockBodyScroll();

      // Close the modal
      if (modal) {
        modal.classList.remove('is-open');
        modal.setAttribute('aria-hidden', 'true');
        const activeTrigger = document.querySelector(`[data-offer-trigger="${modal.id}"]`);
        if (activeTrigger) activeTrigger.setAttribute('aria-expanded', 'false');
      }

      // Smoothly scroll to the target element
      scrollToTarget(targetEl);
      return;
    }

    // 3. If standard URL navigation (without an on-page match)
    if (href && !href.startsWith('#')) {
      if (modal) closeOfferModal(modal, null, true);
    }
  }

  function initExclusiveOfferModals() {
    // Delegated click handling on document
    document.addEventListener('click', function (e) {
      // 0. Ignore clicks on secondary redirection button (let link navigate normally)
      if (e.target.closest('[data-offer-secondary-btn], .exclusive-offer-banner__btn-secondary')) {
        return;
      }

      // 1. Open trigger
      const trigger = e.target.closest('[data-offer-trigger]');
      if (trigger) {
        e.preventDefault();
        const modalId = trigger.getAttribute('data-offer-trigger');
        const modal = document.getElementById(modalId);
        if (modal) {
          openOfferModal(modal, trigger);
        }
        return;
      }

      // 2. Close trigger (close button, backdrop, drag bar)
      const closeBtn = e.target.closest('[data-offer-close]');
      if (closeBtn) {
        e.preventDefault();
        const modal = closeBtn.closest('.exclusive-offer-modal');
        if (modal) {
          closeOfferModal(modal, null, false);
        }
        return;
      }

      // 3. CTA button inside modal
      const ctaBtn = e.target.closest('[data-offer-cta-btn]');
      if (ctaBtn) {
        handleCtaClick(e, ctaBtn);
      }
    });

    // Keyboard listeners (Escape to close, Enter/Space to open)
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        const activeModal = document.querySelector('.exclusive-offer-modal.is-open');
        if (activeModal) closeOfferModal(activeModal, null, false);
      } else if (e.key === 'Enter' || e.key === ' ') {
        const activeEl = document.activeElement;
        if (activeEl && activeEl.matches('[data-offer-trigger]') && !activeEl.closest('a, [data-offer-secondary-btn]')) {
          e.preventDefault();
          const modalId = activeEl.getAttribute('data-offer-trigger');
          const modal = document.getElementById(modalId);
          if (modal) {
            openOfferModal(modal, activeEl);
          }
        }
      }
    });

    // Shopify Theme Editor preview sync
    document.addEventListener('shopify:block:select', function (e) {
      const blockId = e.detail?.blockId;
      if (!blockId) return;
      const modal = document.querySelector(`.exclusive-offer-modal[id*="${blockId}"]`);
      if (modal) openOfferModal(modal);
    });

    document.addEventListener('shopify:block:deselect', function (e) {
      const blockId = e.detail?.blockId;
      if (!blockId) return;
      const modal = document.querySelector(`.exclusive-offer-modal[id*="${blockId}"]`);
      if (modal) closeOfferModal(modal, null, false);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initExclusiveOfferModals);
  } else {
    initExclusiveOfferModals();
  }
})();
